import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "How much does a website cost?",
  "How fast can you launch?",
  "What's included in Starter?",
];

const GREETING = {
  role: "assistant",
  content: "Hi there! I'm Avi, Avora's AI assistant. Ask me anything about our services, pricing, or how we get small businesses online fast.",
};

const getSessionId = () => {
  let id = localStorage.getItem("avora-chat-session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("avora-chat-session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          }
          if (data.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: data.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect just now. Please try again in a moment." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 260, damping: 20 }}
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-gradient-to-br from-indigo to-mint text-white shadow-[0_12px_32px_rgba(91,95,239,0.4)] flex items-center justify-center hover:scale-105 transition-transform duration-300"
        aria-label="Open chat"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-[88px] right-5 z-[60] w-[calc(100vw-40px)] max-w-[380px] h-[520px] max-h-[70vh] bg-white rounded-3xl shadow-[0_24px_64px_rgba(20,22,46,0.25)] border border-ink/5 flex flex-col overflow-hidden"
          >
            <div className="bg-ink px-5 py-4 flex items-center gap-3 shrink-0">
              <img src="/assets/icon_mark.png" alt="Avora" className="w-9 h-9 rounded-xl" />
              <div>
                <p className="text-white font-display font-semibold text-sm">Avi — Avora Assistant</p>
                <p className="text-mint text-xs flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-mint inline-block" /> Online, replies instantly
                </p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-indigo text-white rounded-br-md"
                        : "bg-cloud text-ink rounded-bl-md"
                    }`}
                  >
                    {m.content
                    ? m.content.replace(/\*\*/g, "")
                    : streaming && i === messages.length - 1
                      ? <Loader2 className="w-4 h-4 animate-spin text-slate" />
                      : null}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`chat-suggestion-${s.slice(0, 12).toLowerCase().replace(/[^a-z]/g, "-")}`}
                      className="text-xs font-medium text-indigo border border-indigo/30 rounded-full px-3 py-1.5 hover:bg-indigo hover:text-white transition-colors duration-200"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-ink/5 flex gap-2 shrink-0"
              data-testid="chat-input-form"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about services, pricing..."
                data-testid="chat-message-input"
                className="flex-1 min-w-0 bg-cloud rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo/40"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-send-button"
                className="w-10 h-10 shrink-0 rounded-full bg-indigo text-white flex items-center justify-center hover:bg-ink transition-colors duration-200 disabled:opacity-40"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
